import { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Spin,
  Alert,
  Tabs,
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Empty,
} from 'antd';
import { PlayCircleOutlined, FileTextOutlined } from '@ant-design/icons';
import { mappingApi } from '../../services/api/mapping';

const { Text } = Typography;

interface Props {
  mappingKey: string;
  pdfHash: string;
  pdfName?: string;
  tenantId?: string;
  open: boolean;
  onClose: () => void;
  /**
   * When provided, test this candidate template data (unsaved changes) via
   * testTemplateCandidate instead of the saved template. Used by the box editor.
   */
  candidateData?: Record<string, any>;
}

interface ExtractionResult {
  headerFields: { field: string; value: string }[];
  lineItems: Record<string, string>[];
  lineItemColumns: string[];
}

function parseExtraction(
  extraction: any,
  mappingKey: string
): ExtractionResult {
  const empty: ExtractionResult = {
    headerFields: [],
    lineItems: [],
    lineItemColumns: [],
  };
  if (!extraction) return empty;

  const keyData = extraction[mappingKey];
  if (!keyData?.data?.[0]) return empty;

  const doc = keyData.data[0];
  const headerFields: { field: string; value: string }[] = [];
  let lineItems: Record<string, string>[] = [];
  let lineItemColumns: string[] = [];

  Object.entries(doc).forEach(([field, value]) => {
    if (field === 'lineItems') {
      if (Array.isArray(value) && value.length > 0) {
        lineItems = value as Record<string, string>[];
        lineItemColumns = Object.keys(value[0] || {});
      }
    } else if (value != null && value !== '') {
      headerFields.push({ field, value: String(value) });
    }
  });

  return { headerFields, lineItems, lineItemColumns };
}

export function TestPreviewModal({
  mappingKey,
  pdfHash,
  pdfName,
  tenantId,
  open,
  onClose,
  candidateData,
}: Props) {
  const [loading, setLoading] = useState(false);
  // Normalized to { extraction, text } shape. For candidate tests `extraction`
  // is synthesized as { [mappingKey]: { data: [...] } } so parseExtraction works.
  const [result, setResult] = useState<{
    extraction: any;
    text: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const runTest = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      if (candidateData) {
        // Test unsaved candidate template (box editor) — non-destructive
        const res = await mappingApi.testTemplateCandidate(
          mappingKey,
          candidateData,
          tenantId
        );
        setResult({
          extraction: { [mappingKey]: { data: res.data?.data ?? [] } },
          text: '',
        });
      } else {
        // Test the saved template against the PDF in S3
        const res = await mappingApi.testByHash(mappingKey, pdfHash, tenantId);
        setResult(res.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Test failed');
    } finally {
      setLoading(false);
    }
  };

  // Re-run whenever the modal opens, or the PDF/candidate changes.
  useEffect(() => {
    if (open) runTest();
  }, [open, pdfHash, candidateData]);

  const parsed = result ? parseExtraction(result.extraction, mappingKey) : null;

  const numericFields = [
    'qty',
    'price',
    'total',
    'tax',
    'discount',
    'subtotal',
    'amount',
  ];
  const isNumeric = (col: string) =>
    numericFields.some((n) => col.toLowerCase().includes(n));

  return (
    <Modal
      title={
        <span>
          <PlayCircleOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          Test Extraction
          {pdfName && (
            <Tag color="blue" style={{ marginLeft: 8, fontWeight: 'normal' }}>
              {pdfName.substring(0, 12)}…
            </Tag>
          )}
        </span>
      }
      open={open}
      onCancel={onClose}
      width={900}
      footer={
        <Button
          onClick={() => runTest(pdfHash)}
          loading={loading}
          icon={<PlayCircleOutlined />}
        >
          Re-run
        </Button>
      }
    >
      {loading && (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 12, color: '#888' }}>
            Running extraction…
          </div>
        </div>
      )}

      {!loading && error && (
        <Alert
          type="error"
          message="Extraction failed"
          description={error}
          showIcon
        />
      )}

      {!loading && parsed && (
        <Tabs
          defaultActiveKey="extraction"
          items={[
            {
              key: 'extraction',
              label: 'Extracted Fields',
              children: (
                <div style={{ overflowY: 'auto', maxHeight: 500 }}>
                  {/* Header fields — 2-column grid, mirrors VerificationSummary */}
                  <Card
                    size="small"
                    title="Document Details"
                    style={{ marginBottom: 12, fontSize: 13 }}
                  >
                    {parsed.headerFields.length > 0 ? (
                      <Row gutter={[8, 8]}>
                        {parsed.headerFields.map(({ field, value }) => (
                          <Col span={12} key={field}>
                            <Text style={{ fontSize: 13 }}>
                              <strong>{field}:</strong> {value}
                            </Text>
                          </Col>
                        ))}
                      </Row>
                    ) : (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="No header fields extracted — check template coordinates or patterns"
                      />
                    )}
                  </Card>

                  {/* Line items table — mirrors VerificationSummary */}
                  {parsed.lineItemColumns.length > 0 && (
                    <Card
                      size="small"
                      title={`Line Items (${parsed.lineItems.length})`}
                    >
                      <table
                        style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: 12,
                        }}
                      >
                        <thead>
                          <tr style={{ borderBottom: '1px solid #e1e3e5' }}>
                            {parsed.lineItemColumns.map((col) => (
                              <th
                                key={col}
                                style={{
                                  padding: '8px',
                                  textAlign: isNumeric(col) ? 'right' : 'left',
                                }}
                              >
                                {col}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {parsed.lineItems.map((row, i) => (
                            <tr
                              key={i}
                              style={{ borderBottom: '1px solid #f1f2f3' }}
                            >
                              {parsed.lineItemColumns.map((col) => (
                                <td
                                  key={col}
                                  style={{
                                    padding: '4px 8px',
                                    textAlign: isNumeric(col)
                                      ? 'right'
                                      : 'left',
                                    maxWidth:
                                      col === 'desc'
                                        ? 200
                                        : isNumeric(col)
                                        ? 100
                                        : 120,
                                    wordBreak:
                                      col === 'desc' ? 'break-word' : 'normal',
                                  }}
                                >
                                  {row[col] ?? '-'}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Card>
                  )}
                </div>
              ),
            },
            {
              key: 'text',
              label: (
                <span>
                  <FileTextOutlined /> PDF Text
                </span>
              ),
              children: result?.text ? (
                <pre
                  style={{
                    maxHeight: 420,
                    overflow: 'auto',
                    background: '#f5f5f5',
                    padding: 12,
                    borderRadius: 4,
                    fontSize: 12,
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {result.text}
                </pre>
              ) : (
                <Empty description="No text extracted — PDF may be image-only (requires OCR)" />
              ),
            },
          ]}
        />
      )}
    </Modal>
  );
}
